import React from 'react'
import { Col, Row, Button, Card, CardBody } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import moment from 'moment-timezone'
import 'moment/locale/fi'
import BugReportIcon from '@mui/icons-material/BugReport'
import { Button as MuiButton, Alert } from '@mui/material'
import DateNavigation from '../../components/Filters/DateNavigation'
import {
  buildFreeEvents,
  freeEventsForDate,
  dayKeyRange,
  freeEventsSummary,
  formatFreeEventsSummary,
} from '../../components/utils/freetimes'

// Same convention as every other admin calendar here (CourtsContainer.jsx:95): a FIXED UTC+3
// default zone, paired with accessors that subtract 180 minutes. Net effect is that a UTC
// timestamp's digits render verbatim, all year and regardless of the browser's own timezone -
// this repo's "UTC wall clock". Do not swap it for Europe/Helsinki; the fixed offset is the
// point.
//
// This is an import-time side effect on a shared moment-timezone singleton, so the last module
// imported wins process-wide. Every calendar here sets Etc/GMT-3 except
// RecurrentsCalendarContainer.jsx:28, which sets Europe/Helsinki - harmless only because that
// file is imported nowhere. Wire it up and every calendar in the app shifts by an hour each
// winter.
moment.tz.setDefault('Etc/GMT-3')
const localizer = momentLocalizer(moment)

// Grid bounds have to be built the same way as the accessors, or they land in the wrong zone.
// `new Date(1972, 0, 1, 23, 59, 59)` is created in the BROWSER's zone and then read back in
// Etc/GMT-3, which pushes the end past midnight - react-big-calendar then computes a negative
// slot count and throws 'RangeError: Invalid array length' out of getSlotMetrics. Same idiom as
// CourtsContainer.jsx:497-506.
const gridBound = (time) => moment.utc('1972-01-01 ' + time, 'YYYY-MM-DD HH:mm:ss').subtract(180, 'minutes').toDate()
const DAY_MIN = gridBound('06:00:00')
const DAY_MAX = gridBound('23:59:59')
const SCROLL_TO = gridBound('08:00:00')

// The node is written outside this repo, so its raw shape is one click away rather than
// something you have to open the Firebase console for. Same gate as
// RecurringInquiriesContainer: a production build served from localhost is still someone
// debugging, a real deploy never is.
const IS_LOCALHOST =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname)

function EmptyToolbar() {
  return <div className='rbc-toolbar'></div>
}

function FreeTimeEvent({ event }) {
  return (
    <span>
      <div>{event.title}</div>
      {event.data && event.data.duration ? (
        <div style={{ fontSize: '0.75em', opacity: 0.85 }}>{event.data.duration} min</div>
      ) : null}
    </span>
  )
}

const sortResources = (a, b) => {
  const ao = a.order === undefined ? 1000 : a.order
  const bo = b.order === undefined ? 1000 : b.order
  if (ao !== bo) return ao - bo
  return (a.displayName || '').localeCompare(b.displayName || '')
}

class CustomerTimesCalendarContainer extends React.Component {
  constructor(props) {
    super(props)
    this.filterRef = React.createRef()
    this.state = {
      // null means "we have not heard back yet", [] means "the node had nothing".
      // The empty state has to tell those apart, so it cannot start as [].
      allEvents: null,
      nodeExists: null,
      raw: null,
      // Filled by probeNext(): the date of the first slot at or after now, or null once we know
      // there is none anywhere. Undefined means we have not looked yet.
      nextDate: undefined,
      // A failed probe is NOT the same as an empty node - saying "this venue produces no free
      // times" because the read was denied would state something untrue.
      nextError: null,
      selectedDate: moment.utc().format('YYYY-MM-DD'),
      categoryFilter: 'all',
      calendarHeight: '75vh',
      showJson: false,
      jsonCopied: false,
    }
  }

  componentDidMount() {
    this.subscribe()
    window.addEventListener('resize', this.updateCalendarHeight)
    this.updateCalendarHeight()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedDate !== this.state.selectedDate) {
      this.subscribe()
    }
    if (prevState.allEvents !== this.state.allEvents || prevState.categoryFilter !== this.state.categoryFilter) {
      this.updateCalendarHeight()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateCalendarHeight)
    this.unsubscribe()
  }

  // RTDB's .on() returns the HANDLER, not an unsubscribe function - detaching means
  // ref.off('value', handler). The `unsubscribe*` naming in CourtsContainer is misleading and
  // sits right next to Firestore refs that really are called as functions.
  unsubscribe = () => {
    if (this.freetimesRef && this.freetimesHandler) {
      this.freetimesRef.off('value', this.freetimesHandler)
    }
    this.freetimesRef = null
    this.freetimesHandler = null
  }

  // One day at a time. The whole reason free times were unusable in Kalenteri is that it
  // subscribed to the entire subtree and then pushed every future slot into a single day's
  // calendar, leaving react-big-calendar to lay out tens of thousands of events.
  subscribe = () => {
    this.unsubscribe()
    const range = dayKeyRange(this.state.selectedDate)
    if (!range) return
    this.freetimesRef = this.props.firebase.listenFreetimesRange(range.startKey, range.endKey)
    this.freetimesHandler = this.freetimesRef.on('value', (snapshot) => {
      if (snapshot && snapshot.exists()) {
        // No JSON.parse(JSON.stringify(...)) clone here: snapshot.val() is already a fresh
        // plain object, and cloning doubled the cost over the whole payload for nothing.
        const raw = snapshot.val()
        this.setState({ allEvents: buildFreeEvents(raw), nodeExists: true, raw })
      } else {
        // The original had no else branch, so an emptied node left stale events on screen
        // forever.
        this.setState({ allEvents: [], nodeExists: false, raw: null }, this.probeNext)
      }
    })
  }

  // One child read, only when a day came back empty. Answers "is there anything in this node at
  // all", which is the difference between a quiet Tuesday and a venue that produces no free
  // times - and gives the admin somewhere to jump to.
  probeNext = async () => {
    if (this.state.nextDate !== undefined) return
    try {
      const snapshot = await this.props.firebase.getNextFreetime(moment.utc().format('X'))
      const value = snapshot && snapshot.exists() ? snapshot.val() : null
      const key = value ? Object.keys(value)[0] : null
      this.setState({ nextDate: key ? moment.utc(key, 'X').format('YYYY-MM-DD') : null, nextError: null })
    } catch (error) {
      console.error('freetimes probe failed', error)
      this.setState({ nextDate: null, nextError: error && error.message ? error.message : 'tuntematon virhe' })
    }
  }

  updateCalendarHeight = () => {
    if (this.filterRef && this.filterRef.current) {
      const filterHeight = this.filterRef.current.clientHeight
      const navbarHeight = 62
      const padding = 30
      const available = window.innerHeight - filterHeight - navbarHeight - padding
      this.setState({ calendarHeight: Math.max(available, 300) + 'px' })
    }
  }

  // Both accessors exist because the events carry UTC-wall-clock strings - see the setDefault
  // note at the top of the file.
  startAccessor = (event) => moment(event.start).subtract(180, 'minutes').toDate()

  endAccessor = (event) => moment(event.end).subtract(180, 'minutes').toDate()

  moveDate = (days) => {
    let date
    if (days > 0) {
      date = moment.utc(this.state.selectedDate).add(days, 'days')
    } else if (days < 0) {
      date = moment.utc(this.state.selectedDate).subtract(-days, 'days')
    } else {
      date = moment.utc()
    }
    if (date.isValid()) this.setState({ selectedDate: date.format('YYYY-MM-DD') })
  }

  handleDateChange = (event) => {
    const value = event && event.target ? event.target.value : null
    if (!value) return
    const date = moment.utc(value, 'YYYY-MM-DD', true)
    if (date.isValid()) this.setState({ selectedDate: date.format('YYYY-MM-DD') })
  }

  resources = () => {
    const all = (this.props.customerdata && this.props.customerdata.resources) || []
    const filtered =
      this.state.categoryFilter && this.state.categoryFilter !== 'all'
        ? all.filter((r) => r.category === this.state.categoryFilter)
        : all
    return [...filtered].sort(sortResources)
  }

  categories = () => {
    const all = (this.props.customerdata && this.props.customerdata.resources) || []
    return [...new Set(all.map((r) => r.category).filter(Boolean))]
  }

  events = () => {
    const forDay = freeEventsForDate(this.state.allEvents || [], this.state.selectedDate)
    if (!this.state.categoryFilter || this.state.categoryFilter === 'all') return forDay
    const ids = new Set(this.resources().map((r) => r.id))
    return forDay.filter((e) => ids.has(e.resourceId))
  }

  // Free slots come out solid black in Kalenteri (CourtsContainer.jsx:1500-1502), which read as
  // "not a reservation" among reservations. On their own grid they only have to be legible.
  eventStyleGetter = () => ({
    style: {
      backgroundColor: 'rgba(33, 150, 83, 0.85)',
      borderColor: 'rgb(27, 122, 68)',
      color: 'white',
    },
  })

  copyJson = async () => {
    if (!this.state.raw) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.state.raw, null, 2))
      this.setState({ jsonCopied: true })
    } catch (error) {
      console.error(error)
    }
  }

  getCategoryFilters = () => {
    const buttons = this.categories().map((c) => (
      <Button
        key={c}
        className='btn-round'
        color='info'
        outline
        type='button'
        onClick={() => this.setState({ categoryFilter: c })}
        active={this.state.categoryFilter === c}
      >
        {c}
      </Button>
    ))
    buttons.unshift(
      <Button
        key='all'
        className='btn-round'
        color='info'
        outline
        type='button'
        onClick={() => this.setState({ categoryFilter: 'all' })}
        active={!this.state.categoryFilter || this.state.categoryFilter === 'all'}
      >
        Kaikki
      </Button>
    )
    return buttons
  }

  renderStatus = () => {
    const events = this.events()
    if (this.state.allEvents === null) {
      return <span>Haetaan myytäviä aikoja…</span>
    }
    // An absent node and an empty day are different problems, and saying "none today" for a
    // venue that produces no free times at all would be misleading. probeNext() tells them
    // apart with a single child read.
    if (!events.length) {
      if (this.state.nextDate === undefined) {
        return (
          <Alert severity='info' sx={{ mt: 1 }}>
            Ei myytäviä aikoja tälle päivälle.
          </Alert>
        )
      }
      if (this.state.nextError) {
        return (
          <Alert severity='error' sx={{ mt: 1 }}>
            {'Myytävien aikojen lukeminen epäonnistui: ' + this.state.nextError}
          </Alert>
        )
      }
      if (this.state.nextDate === null) {
        return (
          <Alert severity='warning' sx={{ mt: 1 }}>
            Tälle paikalle ei ole tuotettu myytäviä aikoja lainkaan.
          </Alert>
        )
      }
      return (
        <Alert
          severity='info'
          sx={{ mt: 1 }}
          action={
            <MuiButton size='small' onClick={() => this.setState({ selectedDate: this.state.nextDate })}>
              Siirry
            </MuiButton>
          }
        >
          {'Ei myytäviä aikoja tälle päivälle. Seuraavat: ' +
            moment.utc(this.state.nextDate).format('D.M.YYYY')}
        </Alert>
      )
    }
    return <strong>{formatFreeEventsSummary(freeEventsSummary(events))}</strong>
  }

  render() {
    const events = this.events()
    return (
      <div className='content'>
        <div ref={this.filterRef}>
          <Row>
            <Col md='4' className='align-middle mb-2 mt-2'>
              <DateNavigation
                selectedDate={this.state.selectedDate}
                moveDate={this.moveDate}
                handleDateChange={this.handleDateChange}
                isMobile={false}
              />
            </Col>
            <Col md='8' className='align-middle mb-2 mt-2'>
              <div style={{ marginTop: 8 }}>{this.getCategoryFilters()}</div>
            </Col>
          </Row>
          <Row>
            <Col md='12'>{this.renderStatus()}</Col>
          </Row>
          {IS_LOCALHOST && (
            <Row>
              <Col md='12'>
                <MuiButton
                  size='small'
                  startIcon={<BugReportIcon />}
                  onClick={() => this.setState({ showJson: !this.state.showJson, jsonCopied: false })}
                >
                  {this.state.showJson ? 'Piilota JSON' : 'Näytä JSON'}
                </MuiButton>
                {this.state.showJson && (
                  <MuiButton size='small' onClick={this.copyJson}>
                    {this.state.jsonCopied ? 'Kopioitu' : 'Kopioi'}
                  </MuiButton>
                )}
              </Col>
            </Row>
          )}
          {IS_LOCALHOST && this.state.showJson && (
            <Card>
              <CardBody>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: 12,
                    maxHeight: 260,
                    overflow: 'auto',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(this.state.raw, null, 2)}
                </pre>
              </CardBody>
            </Card>
          )}
        </div>
        <Calendar
          style={{ height: this.state.calendarHeight, backgroundColor: '#FFFFFF' }}
          localizer={localizer}
          events={events}
          startAccessor={this.startAccessor}
          endAccessor={this.endAccessor}
          views={['day']}
          defaultView={Views.DAY}
          step={30}
          date={moment.utc(this.state.selectedDate).toDate()}
          onNavigate={() => {}}
          min={DAY_MIN}
          max={DAY_MAX}
          scrollToTime={SCROLL_TO}
          resources={this.resources()}
          resourceIdAccessor='id'
          resourceTitleAccessor='displayName'
          eventPropGetter={this.eventStyleGetter}
          components={{ event: FreeTimeEvent, toolbar: EmptyToolbar }}
        />
      </div>
    )
  }
}

export default withFirebase(CustomerTimesCalendarContainer)
